<template>
    <v-col class="pageStyle">
        <v-row>
            <v-col>
                <v-form ref="formRef" v-model="isValid" validate-on="input" @submit.prevent="onSubmit">
                    <div class="py-4">
                        <v-img class="mx-auto mb-10" max-width="228" :src="logo" />

                        <v-card class="mx-auto pa-12 pb-8" elevation="8" max-width="448" rounded="lg">
                            <div class="text-subtitle-1 text-medium-emphasis mb-4">Introdu codul</div>

                            <div class="otp-wrap mb-2">
                                <v-text-field v-for="i in 4" :key="'d' + (i - 1)" :ref="'d' + (i - 1)"
                                    v-model="digits[i - 1]" variant="outlined" density="compact" center-affix
                                    class="otp-box" :rules="[required, oneDigit]" maxlength="1" inputmode="numeric"
                                    pattern="[0-9]*" @keydown="onKeydown($event, i - 1)" @input="onInput($event, i - 1)"
                                    @paste="onPaste($event)" />
                            </div>

                            <div class="d-flex justify-space-between align-center mb-4">
                                <span class="text-caption text-medium-emphasis">Cod din 4 cifre</span>
                                <button class="text-button" :disabled="resendDisabled" @click="onResend" type="button">
                                    <span v-if="resendDisabled">Retrimite codul ({{ resendSeconds }}s)</span>
                                    <span v-else>Retrimite codul</span>
                                </button>
                            </div>

                            <!-- câmp ascuns pentru validarea întregului cod -->
                            <v-text-field v-model="fullCode" class="d-none" :rules="[required, codeRule]"
                                hide-details />

                            <v-btn block class="mb-8" color="blue" size="large" variant="tonal" type="submit"
                                :disabled="!isValid || loading" :loading="loading">
                                Trimite
                            </v-btn>

                            <div v-if="message" class="text-body-2 text-medium-emphasis">{{ message }}</div>
                            <div v-if="error" class="text-body-2 text-error">{{ error }}</div>
                        </v-card>
                    </div>
                </v-form>
            </v-col>
        </v-row>
    </v-col>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ForgotPassword from "@/services/AUTH/ForgotPassword";

export default defineComponent({
    name: "ResetVerifyOptions",
    props: {
        logo: { type: String, default: "" },
        resendCooldown: { type: Number, default: 60 },
    },
    data() {
        return {
            identifier: "",
            loading: false,
            message: "",
            error: "",
            isValid: false,
            digits: ["", "", "", ""] as string[],
            resendDisabled: false,
            resendSeconds: 0,
            timer: null as any,
        };
    },
    computed: {
        fullCode(): string {
            return this.digits.join("");
        },
    },
    mounted() {
        const qid = (this.$route.query.id as string) || "";
        this.identifier = qid.trim();
        setTimeout(() => (this.$refs["d0"] as any)?.focus?.(), 50);
        this.startResendTimer();
    },
    beforeUnmount() {
        this.stopResendTimer();
    },
    methods: {
        required(v: string) {
            return (!!v && v.toString().length > 0) || "Câmp obligatoriu";
        },
        oneDigit(v: string) {
            return /^\d?$/.test(v ?? "") || "Doar cifre";
        },
        codeRule(v: string) {
            return /^\d{4}$/.test(v ?? "") || "Codul trebuie să aibă 4 cifre";
        },

        focusIndex(i: number) {
            const r = this.$refs[`d${i}`] as any;
            r?.focus?.();
            r?.select?.();
        },

        onInput(e: Event, idx: number) {
            const input = e.target as HTMLInputElement;
            let val = (input.value || "").replace(/\D/g, "");
            if (val.length > 1) val = val.slice(-1);
            this.digits[idx] = val;
            if (val && idx < 3) this.focusIndex(idx + 1);
        },

        onKeydown(e: KeyboardEvent, idx: number) {
            const input = e.target as HTMLInputElement;
            if (e.key === "Backspace") {
                if (!input.value && idx > 0) {
                    this.digits[idx - 1] = "";
                    this.focusIndex(idx - 1);
                    e.preventDefault();
                }
                return;
            }
            if (e.key === "ArrowLeft" && idx > 0) {
                this.focusIndex(idx - 1); e.preventDefault(); return;
            }
            if (e.key === "ArrowRight" && idx < 3) {
                this.focusIndex(idx + 1); e.preventDefault(); return;
            }
            if (!/^\d$/.test(e.key) && !["Tab", "Enter"].includes(e.key)) {
                e.preventDefault();
            }
        },

        onPaste(e: ClipboardEvent) {
            const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
            if (!text) return;
            e.preventDefault();
            const arr = text.slice(0, 4).split("");
            for (let i = 0; i < 4; i++) this.digits[i] = arr[i] ?? "";
            this.focusIndex(arr.length >= 4 ? 3 : arr.length);
        },

        // resend
        startResendTimer() {
            this.resendDisabled = true;
            this.resendSeconds = this.resendCooldown;
            this.timer = setInterval(() => {
                this.resendSeconds -= 1;
                if (this.resendSeconds <= 0) {
                    this.stopResendTimer();
                    this.resendDisabled = false;
                }
            }, 1000);
        },
        stopResendTimer() {
            if (this.timer) { clearInterval(this.timer); this.timer = null; }
        },
        async onResend() {
            if (this.resendDisabled) return;
            try {
                this.resendDisabled = true;
                await ForgotPassword.request(this.identifier);     // ← retrimitem
                this.message = "Dacă adresa există, codul a fost retrimis.";
            } catch (e) {
                console.error(e);
                this.error = "Nu am putut retrimite codul. Încearcă din nou.";
                this.resendDisabled = false;
                return;
            }
            this.startResendTimer();
        },

        async onSubmit() {
            const result = await (this.$refs.formRef as any)?.validate?.();
            if (!result?.valid) return;

            if (!this.identifier?.trim()) {
                this.error = "Lipsește emailul (identifier).";
                return;
            }

            this.loading = true;
            this.message = "";
            this.error = "";

            try {
                this.message = "Verific codul…";
                const resp = await ForgotPassword.confirm(this.identifier.trim(), this.fullCode);

                this.message = "Parola a fost resetată și trimisă pe email. Vei fi redirecționat...";

                setTimeout(() => {
                    this.$router.push("/login");
                }, 3000);

            } catch (e) {
                console.error(e);
                this.error = "Cod invalid sau expirat.";
            } finally {
                this.loading = false;
            }
        }

    },
});
</script>

<style scoped>
.otp-wrap {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
}

.otp-box :deep(input) {
    text-align: center !important;
    font-size: 32px;
    letter-spacing: 2px;
    padding: 10px 0;
}

.text-button {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    font: inherit;
    text-decoration: underline;
}

.text-button[disabled] {
    opacity: 0.6;
    cursor: default;
    text-decoration: none;
}

.d-none {
    display: none !important;
}
</style>
